import React from 'react';
import { Card, CardHeader, CardBody, CardFooter, Button } from './ui';
import { JobStatusBadge } from './StatusBadge';

export interface Job {
  id: number;
  srJobId: string;
  title: string;
  department?: string | null;
  location?: string | null;
  status: string;
  description?: string | null;
  requirements?: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface JobCardProps {
  job: Job;
  applicationCount?: number;
  pendingReviewCount?: number;
  onView?: (job: Job) => void;
  onEdit?: (job: Job) => void;
  className?: string;
  compact?: boolean;
}

export const JobCard: React.FC<JobCardProps> = ({
  job,
  applicationCount,
  pendingReviewCount,
  onView,
  onEdit,
  className = '',
  compact = false,
}) => {
  const createdDate = typeof job.createdAt === 'string'
    ? new Date(job.createdAt)
    : job.createdAt;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (compact) {
    return (
      <div
        className={`
          flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200
          hover:border-gray-300 transition-colors cursor-pointer
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        onClick={() => onView?.(job)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onView?.(job)}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {job.title}
            </h3>
            <JobStatusBadge status={job.status} size="sm" showDot={false} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {[job.department, job.location].filter(Boolean).join(' - ') || 'No location'}
          </p>
        </div>
        {applicationCount !== undefined && (
          <div className="ml-4 text-right">
            <p className="text-sm font-medium text-gray-900">{applicationCount}</p>
            <p className="text-xs text-gray-500">applications</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className={className} variant="default">
      <CardHeader
        title={
          <div className="flex items-center gap-2">
            <span className="truncate">{job.title}</span>
            <JobStatusBadge status={job.status} size="sm" />
          </div>
        }
        description={
          [job.department, job.location].filter(Boolean).join(' - ') || 'No department or location'
        }
      />
      <CardBody>
        {job.description ? (
          <p className="text-sm text-gray-600 line-clamp-3">{job.description}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">No description available</p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-4">
          {applicationCount !== undefined && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-2xl font-semibold text-gray-900">
                {applicationCount}
              </p>
              <p className="text-xs text-gray-500 mt-1">Total Applications</p>
            </div>
          )}
          {pendingReviewCount !== undefined && (
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-2xl font-semibold text-amber-700">
                {pendingReviewCount}
              </p>
              <p className="text-xs text-gray-500 mt-1">Pending Review</p>
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-gray-500">
          <span>ID: {job.srJobId}</span>
          <span className="mx-2">|</span>
          <span>Created: {formatDate(createdDate)}</span>
        </div>
      </CardBody>
      {(onView || onEdit) && (
        <CardFooter>
          {onEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit(job)}>
              Edit
            </Button>
          )}
          {onView && (
            <Button variant="secondary" size="sm" onClick={() => onView(job)}>
              View Details
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};

JobCard.displayName = 'JobCard';
